import { DmObjectType } from '@bimser/common';
import { BCButton, BCCombobox, BCNotification, findMessage } from '@bimser/components';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import * as React from 'react';
import Style from '../assets/style.scss';
import IFileCompareProps from '../entities/IFileCompareProps';
import { ObjectSelectPopoverContainer } from 'modules/dm/components/explorer/components/objectSelectPopover/containers';
import ICompareObject from '../entities/ICompareObject';
import { IDmVersion } from 'modules/dm/components/viewer/entities/IDmVersion';
import ISelectedVersion from '../entities/ISelectedVersion';
import { isExtensionEqual } from '../sagas/helper/comparisonHelper';
import { useContainerSize } from '@bimser/core';
dayjs.extend(localizedFormat);

const CompareModal = (props: IFileCompareProps) => {
	const targetRef = React.useRef<HTMLDivElement>();
	const dimensions = useContainerSize(targetRef);

	const [popoverVisibleLeft, setPopoverVisibleLeft] = React.useState(false);
	const [popoverVisibleRight, setPopoverVisibleRight] = React.useState(false);
	const [type, setType] = React.useState<number>(null);
	const sourceVersionList = React.useMemo(() => {
		return props.sourceObj?.versions.items.map((u: IDmVersion) => ({
			key: u.id,
			text: `Version ${u.version} - ${dayjs(u.createdAt).format('lll').toString()}`,
			value: u.secretKey
		}));
	}, [props.sourceObj?.versions.items]);

	const [targetVersionList, setTargetVersionList] = React.useState<ISelectedVersion[]>([]);

	React.useEffect(() => {
		if (props.targetObj && targetVersionList.length === 0) {
			setTargetVersionList(
				props.targetObj?.versions.items.map((u: IDmVersion) => ({
					key: u.id,
					text: `Version ${u.version} - ${dayjs(u.createdAt).format('lll').toString()}`,
					value: u.secretKey
				}))
			);
		}
	}, [props.targetObj, targetVersionList.length]);

	const renderCompareScreen = () => (
		<div
			id="comparisonComponent"
			style={{ height: '100%' }}
		>
			<iframe
				width="100%"
				height="100%" //
				src={props.url ? props.url : getCompareUrl()}
			></iframe>
		</div>
	);

	const getCompareUrl = () => {
		const webUrl = (window as any).serviceConfig.services.web.baseURL;
		const html5viewerUrl = (window as any).serviceConfig.urls.html5viewer;

		const sourceUrl = props.sourceObj ? webUrl + '/' + props.sourceObj.url : '';
		const targetUrl = props.targetObj ? webUrl + '/' + props.targetObj.url : '';
		return `${html5viewerUrl}/comparison/params?ep=${
			props.sourceObj ? props.sourceObj.name : ''
		}&${props.targetObj ? props.targetObj.name : ''}&${sourceUrl}&${targetUrl}&false&false`;
	};

	const handlePopoverClose = () => {
		if (popoverVisibleLeft || popoverVisibleRight) {
			setPopoverVisibleLeft(false);
			setPopoverVisibleRight(false);
		}
	};

	const onReturnFunction = (data?: ICompareObject[]) => {
		if (data.length > 0) {
			if (
				isExtensionEqual(
					props.sourceObj.name.split('.').pop().toLowerCase(),
					data[0].name.split('.').pop().toLowerCase()
				)
			) {
				setPopoverVisibleLeft(false);
				setPopoverVisibleRight(false);
				const payloadData = {
					panelId: props.panelGuid,
					data: {
						secretKey: data[0].secretKey,
						name: data[0].name,
						type: type,
						modalId: props.panelGuid
					}
				};

				props.onActionSelectFile(payloadData);
			} else {
				BCNotification.warning({
					message: findMessage.get('103589'),
					description: findMessage.get('103590'),
					autoClose: 2000
				});
			}
		}
	};

	const onVersionChangeFunction = (
		selectedVersion: ISelectedVersion,
		data: ICompareObject,
		type: number
	) => {
		const payloadData = {
			panelId: props.panelGuid,
			data: {
				secretKey: data.secretKey,
				versionSecretKey: selectedVersion.value,
				name: data.name,
				versions: data.versions,
				type: type,
				modalId: props.panelGuid,
				url: data.url
			}
		};

		props.onVersionChange(payloadData);
	};

	const popoverShowClick = (type?: number) => {
		if (type == 0) {
			setPopoverVisibleLeft(true);
			setPopoverVisibleRight(false);
			setType(type);
		} else if (type == 1) {
			setPopoverVisibleRight(true);
			setPopoverVisibleLeft(false);
			setType(type);
		}
	};
	const handleSourceChange = event => {
		const selectedVersion = event.senderArgs.option;
		onVersionChangeFunction(selectedVersion, props.sourceObj, 0);
	};

	const handleTargetChange = event => {
		const selectedVersion = event.senderArgs.option;
		onVersionChangeFunction(selectedVersion, props.targetObj, 1);
	};

	const getModalContent = (type?: number): JSX.Element => {
		const visible = type == 0 ? popoverVisibleLeft : popoverVisibleRight;
		return (
			<>
				<ObjectSelectPopoverContainer
					willClose={handlePopoverClose}
					parentId={props.panelGuid}
					isVisible={visible}
					selectType={[DmObjectType.File]}
					onReturn={onReturnFunction}
					multiple={false}
					placement="right"
				>
					<BCButton
						type="default"
						onClick={() => popoverShowClick(type)}
					>
						{findMessage.get('103605')}
					</BCButton>
				</ObjectSelectPopoverContainer>
			</>
		);
	};
	return (
		<div className={Style.comparisonSingleContainer}>
			<div className={Style.viewerHeaderDocName}>
				<div className={Style.documentName}>
					{props.sourceObj ? (
						<div className={Style.flexContainer}>
							<span className={Style.documentNameText}>{props.sourceObj.name}</span>
							{sourceVersionList.length ? (
								<BCCombobox
									value={sourceVersionList[0].value}
									className={Style.permissionSelectArea}
									options={sourceVersionList}
									showSearch={false}
									onSelectedChange={handleSourceChange}
								/>
							) : (
								''
							)}
						</div>
					) : (
						''
					)}
				</div>

				<div className={Style.documentName}>
					{props.targetObj ? (
						<div className={Style.flexContainer}>
							<span className={Style.documentNameText}>{props.targetObj.name}</span>
							{targetVersionList.length ? (
								<BCCombobox
									value={targetVersionList[0].value}
									className={Style.permissionSelectArea}
									options={targetVersionList}
									showSearch={false}
									onSelectedChange={handleTargetChange}
								/>
							) : (
								''
							)}
						</div>
					) : (
						<div className={Style.flexContainer}>
							<div className={Style.addButton}>{getModalContent(1)}</div>
						</div>
					)}
				</div>
			</div>
			<div
				key={dimensions.width}
				id="singleComparisonDiv"
				style={{ height: '100%' }}
				ref={targetRef}
			>
				{renderCompareScreen()}
			</div>
		</div>
	);
};

export default CompareModal;
